import React, { useEffect, useState } from 'react';

interface RangeSliderProps {
	label: string;
	value: number;
	onChange: (value: number) => void;
	min?: number;
	max?: number;
	step?: number;
	unit?: string; // '%' or '$'
	precision?: number; // decimal places
	className?: string;
	disabled?: boolean;
}

const RangeSlider: React.FC<RangeSliderProps> = ({
	label,
	value,
	onChange,
	min = 0,
	max = 10,
	step = 0.01,
	unit = '%',
	precision = 2,
	className = '',
	disabled = false,
}) => {
	// Safety check: ensure value is a valid number
	const safeValue = typeof value === 'number' && !isNaN(value) ? value : min;
	const [displayValue, setDisplayValue] = useState(
		safeValue.toFixed(precision),
	);

	useEffect(() => {
		const currentSafeValue =
			typeof value === 'number' && !isNaN(value) ? value : min;
		setDisplayValue(currentSafeValue.toFixed(precision));
	}, [value, precision, min]);

	const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = parseFloat(e.target.value);
		onChange(newValue);
		setDisplayValue(newValue.toFixed(precision));
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const rawValue = e.target.value;
		setDisplayValue(rawValue);
	};

	const handleInputBlur = () => {
		const numValue = parseFloat(displayValue);
		if (!isNaN(numValue)) {
			// Allow values beyond max, but clamp to min
			const clampedValue = Math.max(numValue, min);
			onChange(clampedValue);
			setDisplayValue(clampedValue.toFixed(precision));
		} else {
			const currentSafeValue =
				typeof value === 'number' && !isNaN(value) ? value : min;
			setDisplayValue(currentSafeValue.toFixed(precision));
		}
	};

	const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			handleInputBlur();
		}
	};

	// Calculate percentage for slider position
	// If value exceeds max, show full width (100%)
	// If value is at or below min, show no progress (0%)
	const percentage =
		safeValue >= max
			? 100
			: safeValue <= min
				? 0
				: ((safeValue - min) / (max - min)) * 100;

	return (
		<div className={`space-y-1 ${className}`}>
			{/* Slider Row with Icon and Input */}
			<div className="flex items-center space-x-4">
				<div className="flex-shrink-0 w-10 h-10 bg-amber-100/50 rounded-lg flex items-center justify-center">
					<span className="text-amber-800 text-lg">
						{unit === '$' ? '$' : '%'}
					</span>
				</div>

				<div className="flex-1 mb-1">
					{/* Label Above Slider Only */}
					<div className="flex items-center">
						<span className="text-sm text-gray-900">{label}</span>
					</div>
					{/* Slider */}
					<div className="flex-1 relative mt-1">
						{/* Slider track background */}
						<div className="relative h-1 bg-gray-200 rounded-full">
							{/* Progress fill */}
							<div
								className={`absolute left-0 top-0 h-1 rounded-full transition-all duration-75 pointer-events-none ${
									safeValue >= max ? 'bg-orange-500' : 'bg-purple-500'
								}`}
								style={{ width: `${percentage}%` }}
							/>

							{/* Slider thumb/marker */}
							<div
								className={`absolute w-3 h-3 bg-white rounded-full shadow-sm transition-all duration-75 pointer-events-none transform -translate-y-1 ${
									safeValue >= max
										? 'border-2 border-orange-500'
										: 'border-2 border-purple-500'
								}`}
								style={{
									left:
										safeValue >= max
											? `calc(100% - 10px)`
											: safeValue <= min
												? `0px`
												: `calc(${percentage}% - 10px)`,
								}}
							/>
						</div>

						{/* Slider input - positioned over the track */}
						<input
							type="range"
							min={min}
							max={max}
							step={step}
							value={Math.min(safeValue, max)} // Clamp slider value to max
							onChange={handleSliderChange}
							disabled={disabled}
							className="absolute inset-0 w-full h-2 cursor-pointer disabled:cursor-not-allowed opacity-0"
						/>
					</div>
				</div>
				{/* Input Field with Unit Inside - center aligned */}
				<div className="flex-shrink-0">
					<input
						type="text"
						value={unit === '$' ? `$${displayValue}` : `${displayValue}%`}
						onChange={(e) => {
							const rawValue = e.target.value.replace(/[$%]/g, '');
							setDisplayValue(rawValue);
						}}
						onBlur={() => {
							const numValue = parseFloat(displayValue);
							if (!isNaN(numValue)) {
								// Allow values beyond max, but clamp to min
								const clampedValue = Math.max(numValue, min);
								onChange(clampedValue);
								setDisplayValue(clampedValue.toFixed(precision));
							} else {
								setDisplayValue(value.toFixed(precision));
							}
						}}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								const numValue = parseFloat(displayValue);
								if (!isNaN(numValue)) {
									// Allow values beyond max, but clamp to min
									const clampedValue = Math.max(numValue, min);
									onChange(clampedValue);
									setDisplayValue(clampedValue.toFixed(precision));
								} else {
									const currentSafeValue =
										typeof value === 'number' && !isNaN(value) ? value : min;
									setDisplayValue(currentSafeValue.toFixed(precision));
								}
							}
						}}
						disabled={disabled}
						className={`w-16 px-2 py-1 text-sm text-right border rounded-md focus:outline-none focus:ring-1 ${
							safeValue >= max
								? 'border-orange-300 focus:ring-orange-500 focus:border-orange-500 text-orange-700 bg-orange-50'
								: 'border-gray-300 focus:ring-purple-500 focus:border-purple-500 text-purple-700 bg-white'
						}`}
					/>
				</div>
			</div>
		</div>
	);
};

export default RangeSlider;
